import React, { HTMLAttributes } from 'react';
import cn from 'classnames';

import { Box, SxProps, Theme } from '@mui/material';
import Tooltip, { Props as TooltipProps } from '../Tooltip';
import Button, { ButtonProps } from '../Button';

interface BaseIconButtonProps extends Omit<ButtonProps,
  | 'startIcon'
  | 'endIcon'
  | 'uppercase'
  | 'underline'
  | 'fullWidth'
  | 'title'
> {
  children: React.ReactNode;
  tooltipPlacement?: TooltipProps['placement'];
}

interface PropsWithNodeTitle extends BaseIconButtonProps {
  title: React.ReactNode;
  'aria-label': string;
}

interface PropsWithStringTitle extends BaseIconButtonProps {
  title: string;
}

export type IconButtonProps = PropsWithNodeTitle | PropsWithStringTitle;

/**
 * IconButton is a wrapper around the Button component that allows displaying tooltip when button is disabled
 * Also unsures button receives correct accessibility attributes
 */
const ButtonWrapper = React.forwardRef<
  HTMLDivElement,
  HTMLAttributes<HTMLDivElement> & { 
    'data-test-id'?: string;
    sx?: SxProps<Theme>; 
  }
>((props, ref) => {
  const {
    children,
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledby,
    'data-test-id': _dataTestId,
    ...resolvedProps
  } = props;

  return <Box display='inline-flex' {...resolvedProps} ref={ref} className='icon-button-wrapper'>
    {React.cloneElement(children as React.ReactElement, {
      'aria-label': (children as React.ReactElement).props['aria-label'] || ariaLabel,
      'aria-labelledby': ariaLabelledby,
    })}
  </Box>;
});

const IconButton = ({
  children,
  title,
  testID,
  disabled,
  tooltipPlacement,
  color,
  variant = 'text',
  className,
  sx,
  ...props
}: IconButtonProps) => {

  const getColor = (): IconButtonProps['color'] => {
    if (!color) {
      switch (variant) {
        case 'contained':
        case 'tonal':
          return 'primary';
        default:
          return 'secondary';
      }
    }
    return color;
  };

  return (
    <Tooltip
      testID={testID}
      title={title}
      placement={tooltipPlacement}
    >
      <ButtonWrapper sx={sx}>
        <Button
          {...props}
          color={getColor()}
          variant={variant}
          className={cn('iconOnly', className)}
          size={props.size}
          testID={testID}
          disabled={disabled}
        >
          {children}
        </Button>
      </ButtonWrapper>
    </Tooltip >
  );
};

export default IconButton;
